// Module: useStrategyOrdersBackfill
// Purpose: Per-strategy paginated order backfill via the gateway-routed
//          OrdersService.getOrders({ strategyInstanceId }) — fixes order-window
//          starvation on the StrategyDeepDive Monitoring/Orders tabs.
//
//          TradingDataContext's scoped useTradingData({strategyInstanceId}) hook
//          only client-filters the shared global latest-100 orders fetch
//          (TradingDataContext.jsx:838 + :922-933, filter at :1302-1307). Under
//          real order volume (~776 paper orders/24h observed) a given strategy's
//          own orders age out of that shared 100-row window, so the filter
//          silently returns [] and TradeHistory's groupOrdersIntoTrades has
//          nothing to pair -> "No trades yet" even with an OPEN position.
//
//          TradingDataContext.jsx:892-920 documents that the scoped hook's
//          per-SID pre-fetch was deliberately REMOVED because it leaked
//          cross-simulation-run orders into LiveAnalysisTab's chart markers,
//          and notes "useTradingData({strategyInstanceId}) has exactly one
//          consumer (LiveAnalysisTab)". That is no longer true — StrategyDeepDive
//          (StrategyDeepDive.jsx) also calls the scoped hook, for its
//          Monitoring/Orders tabs, which have no chart to mis-place markers on
//          and need full (unwindowed) history, not a bar-clock window. This hook
//          gives that consumer its OWN backfill, mirroring LiveAnalysisTab's
//          historicalOrders pagination loop (LiveAnalysisTab.jsx:158-202)
//          instead of re-touching the shared context/scoped-hook internals.

import { useEffect, useState, useCallback, useRef } from 'react';
import OrdersService from '../../api/services/orders';

const PAGE_LIMIT = 200;
const MAX_ORDERS = 1000; // protect browser memory — same cap as LiveAnalysisTab

/**
 * Fetches the full order history for one strategy instance, paginated via
 * skip/limit, through the API Gateway ('/orders', auth:8000) — never a direct
 * microservice call.
 * @param {string|null} strategyInstanceId
 * @returns {{ orders: Array, isLoading: boolean, error: string|null, refetch: Function }}
 */
export function useStrategyOrdersBackfill(strategyInstanceId) {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  //! Monotonic run counter — each fetchAll() call (mount-effect or manual
  //! refetch) captures the CURRENT counter value at call time. A stale run
  //! (superseded by a strategyInstanceId change or a fresh refetch) compares
  //! its captured id against the ref's latest value before every state write
  //! and before continuing its pagination loop. This is deliberately NOT a
  //! shared boolean "active" flag — a boolean has a re-render race: cleanup
  //! sets it false, then the NEXT effect run immediately flips it back to
  //! true before the OLD run's pending await resumes, reviving a stale write.
  //! A monotonically increasing id has no such revival path.
  const runIdRef = useRef(0);

  const fetchAll = useCallback(async () => {
    const myRunId = ++runIdRef.current;
    if (!strategyInstanceId) {
      setOrders([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      let allOrders = [];
      let skip = 0;
      let hasMore = true;

      while (hasMore && runIdRef.current === myRunId) {
        // eslint-disable-next-line no-await-in-loop
        const res = await OrdersService.getOrders({
          strategyInstanceId,
          limit: PAGE_LIMIT,
          skip,
        });
        const fetched = res?.data?.data?.orders || [];
        if (fetched.length > 0) allOrders = allOrders.concat(fetched);
        if (fetched.length < PAGE_LIMIT || allOrders.length >= MAX_ORDERS) {
          hasMore = false;
        } else {
          skip += PAGE_LIMIT;
        }
      }

      if (runIdRef.current === myRunId) setOrders(allOrders);
    } catch (err) {
      console.error('[useStrategyOrdersBackfill] fetch failed', err);
      if (runIdRef.current === myRunId) setError(err?.message || 'Failed to load strategy order history');
    } finally {
      if (runIdRef.current === myRunId) setIsLoading(false);
    }
  }, [strategyInstanceId]);

  useEffect(() => {
    fetchAll();
    // --> Invalidate any in-flight run started by this effect invocation
    //*   (unmount, or strategyInstanceId change before the loop finished).
    return () => { runIdRef.current += 1; };
  }, [fetchAll]);

  return { orders, isLoading, error, refetch: fetchAll };
}
